import {
  faFrown,
  faHandshake,
  faMessagesQuestion,
  faScaleUnbalanced,
  faStar,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons'

import {
  DialoguesSessionFeedbackRating,
  SESSION_FEEDBACK_TYPE_DIALOGUES,
  SESSION_FEEDBACK_TYPE_TUTOR,
  type SessionFeedbackType,
  TutorSessionFeedbackRating,
} from '@/queries/constants'
import type { SerializedRichText } from '@/client/services/richText/types'

import composeRichText from '../richText/composeRichText'
import { getSATBootcampCurriculumUrl } from '../url'
import type { FocusConcept, SessionFeedbackAutomatedHighlight } from './types'

// We ask learners for internal feedback on the 1st, 2nd, etc sessions.
export const SESSIONS_WITH_TUTOR_TO_SHOW_INTERNAL_FEEDBACK = [1, 3, 5, 7, 15, 30]
export const SESSIONS_WITH_DIALOGUES_TO_SHOW_INTERNAL_FEEDBACK = [1, 3, 5, 7, 15, 30]
export const NPS_RATINGS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export enum SessionFeedbackRatingTag {
  MovedTooFast = 'moved-too-fast',
  MovedTooSlow = 'moved-too-slow',
  HardToUnderstand = 'hard-to-understand',
  WrongContent = 'wrong-content',
  NotInteractive = 'not-interactive',
  Unfriendly = 'unfriendly',
  Interactive = 'interactive',
  Fun = 'fun',
  Friendly = 'friendly',
  GoodExplanations = 'good-explanations',
  DidNotUnderstandMaterial = 'did-not-understand-material',
  Patient = 'patient',
  Other = 'other',
}

export const GOOD_RATING_TAGS = [
  SessionFeedbackRatingTag.Friendly,
  SessionFeedbackRatingTag.Fun,
  SessionFeedbackRatingTag.GoodExplanations,
  SessionFeedbackRatingTag.Interactive,
  SessionFeedbackRatingTag.Patient,
]

const BAD_RATING_TAGS = [
  SessionFeedbackRatingTag.HardToUnderstand,
  SessionFeedbackRatingTag.MovedTooFast,
  SessionFeedbackRatingTag.MovedTooSlow,
  SessionFeedbackRatingTag.NotInteractive,
  SessionFeedbackRatingTag.Unfriendly,
  SessionFeedbackRatingTag.WrongContent,
  SessionFeedbackRatingTag.DidNotUnderstandMaterial,
]

export const TUTOR_SESSION_FEEDBACK_RATING_TAG_TO_LABEL: Record<SessionFeedbackRatingTag, string> =
  {
    [SessionFeedbackRatingTag.HardToUnderstand]: 'Hard to understand',
    [SessionFeedbackRatingTag.Friendly]: 'Friendly',
    [SessionFeedbackRatingTag.Fun]: 'Fun',
    [SessionFeedbackRatingTag.GoodExplanations]: 'Good explanations',
    [SessionFeedbackRatingTag.Interactive]: 'Interactive',
    [SessionFeedbackRatingTag.MovedTooFast]: 'Moved too fast',
    [SessionFeedbackRatingTag.MovedTooSlow]: 'Moved too slow',
    [SessionFeedbackRatingTag.NotInteractive]: 'Not interactive',
    [SessionFeedbackRatingTag.Patient]: 'Patient',
    [SessionFeedbackRatingTag.Unfriendly]: 'Unfriendly',
    [SessionFeedbackRatingTag.WrongContent]: 'Not the right content',
    [SessionFeedbackRatingTag.DidNotUnderstandMaterial]: "Tutor didn't understand the material",
    [SessionFeedbackRatingTag.Other]: 'N/A',
  }

export const TUTOR_SESSION_FEEDBACK_RATING_TO_TAGS: Record<
  TutorSessionFeedbackRating,
  SessionFeedbackRatingTag[]
> = {
  [TutorSessionFeedbackRating.NoOpinion]: [],
  [TutorSessionFeedbackRating.NotHelpful]: BAD_RATING_TAGS,
  [TutorSessionFeedbackRating.Helpful]: [...GOOD_RATING_TAGS, ...BAD_RATING_TAGS],
  [TutorSessionFeedbackRating.SuperHelpful]: GOOD_RATING_TAGS,
}

export const RATING_TYPE_TO_LABEL: Record<TutorSessionFeedbackRating, string> = {
  [TutorSessionFeedbackRating.NoOpinion]: "Didn't Attend",
  [TutorSessionFeedbackRating.NotHelpful]: 'Not Helpful',
  [TutorSessionFeedbackRating.Helpful]: 'Helpful',
  [TutorSessionFeedbackRating.SuperHelpful]: 'Super Helpful',
}

export const DIALOGUES_RATING_TYPE_TO_LABEL: Record<DialoguesSessionFeedbackRating, string> = {
  [DialoguesSessionFeedbackRating.NoOpinion]: 'No opinion',
  [DialoguesSessionFeedbackRating.AgreedStruggledToExplore]:
    'Mostly agreed but struggled to explore alternatives',
  [DialoguesSessionFeedbackRating.AgreedAndExplored]:
    'Mostly agreed and explored alternative perspectives',
  [DialoguesSessionFeedbackRating.DifferingViewsOnTopic]: 'Had differing views on the topic',
}

export const SESSION_FEEDBACK_TYPE_TO_LABEL: Record<SessionFeedbackType, Record<number, string>> = {
  [SESSION_FEEDBACK_TYPE_TUTOR]: RATING_TYPE_TO_LABEL,
  [SESSION_FEEDBACK_TYPE_DIALOGUES]: DIALOGUES_RATING_TYPE_TO_LABEL,
}

export const SESSION_FEEDBACK_TYPE_TO_RATING_OPTIONS = {
  [SESSION_FEEDBACK_TYPE_TUTOR]: [
    {
      rating: TutorSessionFeedbackRating.SuperHelpful,
      label: 'Super Helpful',
      icon: faStar,
    },
    {
      rating: TutorSessionFeedbackRating.Helpful,
      label: 'Helpful',
      icon: faThumbsUp,
    },
    {
      rating: TutorSessionFeedbackRating.NotHelpful,
      label: 'Not Helpful',
      icon: faFrown,
    },
  ],
  [SESSION_FEEDBACK_TYPE_DIALOGUES]: [
    {
      rating: DialoguesSessionFeedbackRating.DifferingViewsOnTopic,
      label: 'Had differing views on the topic',
      icon: faScaleUnbalanced,
    },
    {
      rating: DialoguesSessionFeedbackRating.AgreedAndExplored,
      label: 'Mostly agreed and explored alternative perspectives',
      icon: faHandshake,
    },
    {
      rating: DialoguesSessionFeedbackRating.AgreedStruggledToExplore,
      label: 'Mostly agreed but struggled to explore alternatives',
      icon: faMessagesQuestion,
    },
  ],
} as const

export const FOCUS_CONCEPTS = [
  'asking-questions',
  'revoicing-learner-ideas',
  'prompting-for-reasoning',
] as const

export const FOCUS_CONCEPT_TO_DETAILS: Record<
  FocusConcept,
  {
    resourceBlurb?: SerializedRichText
    exampleType: string
    label: string
    peerExamples: Omit<SessionFeedbackAutomatedHighlight, 'timestamp'>[][]
  }
> = {
  'asking-questions': {
    label: `asking questions to get learners' ideas on the table`,
    exampleType: `inviting learners' ideas and questions`,
    resourceBlurb: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Want more ideas for engaging learners? Check out our ', { italic: true }),
        ctx.linkExternal(
          getSATBootcampCurriculumUrl('what-would-you-like-support-with-60'),
          ctx.text('session troubleshooting guide', { italic: true })
        ),
        ctx.text('.')
      )
    ),
    peerExamples: [
      [
        {
          speaker: 'tutor',
          text: 'What does it mean when two functions intersect?',
        },
      ],
      [
        {
          speaker: 'tutor',
          text: 'How would you solve this problem if we know that both of these expressions have to be equal to each other?',
        },
      ],
    ],
  },
  'revoicing-learner-ideas': {
    label: 'revoicing learner ideas',
    exampleType: `building on learners' ideas`,
    resourceBlurb: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Struggling with a specific challenge in your sessions? Please check out our ', {
          italic: true,
        }),
        ctx.linkExternal(
          getSATBootcampCurriculumUrl('what-would-you-like-support-with-60'),
          ctx.text('session troubleshooting guide', { italic: true })
        ),
        ctx.text('.')
      )
    ),
    peerExamples: [
      [
        {
          speaker: 'learner',
          text: 'Is it 4n plus m equals 20?',
        },
        {
          speaker: 'tutor',
          text: 'Yup! It would be 4n plus m is equal to 20.',
        },
      ],
      [
        {
          speaker: 'learner',
          text:
            `It's 11 novels or magazines. So I did the max amount of novels you could do, which is 4. That'd be 16, and then magazines, ` +
            `which is also 4 but that didn't work out. So I'm not too sure.`,
        },
        {
          speaker: 'tutor',
          text:
            ' Yeah, no worries. The guessing and checking is definitely a good way to approach a lot of problems. But for systems of equations, ' +
            'setting it up is just way easier and way faster on the test. So the reason why n plus m is equal to 11 is because she sells novels and magazines. Right?',
        },
      ],
    ],
  },
  'prompting-for-reasoning': {
    label: 'prompting learners for their reasoning',
    exampleType: 'encouraging learners to share their reasoning',
    peerExamples: [
      [
        {
          speaker: 'tutor',
          text: 'Why did you decide to put the x back into the original equation?',
        },
      ],
      [
        {
          speaker: 'tutor',
          text: 'In what way did you guess? What was your process?',
        },
      ],
    ],
  },
}

export const STRUGGLING_TUTOR_TALK_TIME_THRESHOLD = 0.95
